import type { OrderIntent, SideEnum } from '@talos/kyoko';
import {
  EMPTY_ARRAY,
  HStack,
  isMultileg,
  MixpanelEvent,
  PriceLadder,
  UnifiedLiquidityEnum,
  useMixpanel,
  useSecuritiesContext,
  useSecurity,
} from '@talos/kyoko';
import type { IDockviewPanelProps } from 'dockview';
import { useCallback, useEffect, useState } from 'react';
import { useMeasure } from 'react-use';
import { useMarketDataSnapshot } from '../../../../../hooks';
import { useCombinedMarketsAndAccounts } from '../../../../../hooks/useCombinedMarketsAndAccounts';
import { usePrimeFromOrderBook } from '../../../../../hooks/usePrimeFromOrderBook';
import { MULTILEG_MARKET_ACCOUNT } from '../../../../../providers/OMSContext.types';
import type { PriceLadderPanelConfig, SingleSymbolPanelConfig } from '../../../../../types/LayoutConfig';
import { Panel } from '../../../../Layout/Components/Panel';
import { SingleSymbolPanelHeader } from './SingleSymbolPanelHeader';
import { SingleSymbolPanelSettings } from './SingleSymbolPanelSettings';

const HEADER_HEIGHT = 68;
const ROW_MIN_HEIGHT = 20;

export function PriceLadderPanel({ api, params }: IDockviewPanelProps<PriceLadderPanelConfig>) {
  const [showSettings, setShowSettings] = useState(false);
  const {
    aggregation,
    symbol,
    markets,
    marketAccounts,
    priceIncrement,
    customTitle,
    enableUnifiedLiquidity,
    orderPresetID,
    settingsInitialOpen,
    subAccount,
  } = params;
  const { searchableSecurities, securitiesBySymbol } = useSecuritiesContext();
  const security = useSecurity(symbol);
  const [ref, { width, height }] = useMeasure<HTMLDivElement>();
  const [depth, setDepth] = useState(10);
  const mixpanel = useMixpanel();

  // Open settings panel when the panel is first added and `settingsInitialOpen` is true
  useEffect(() => {
    if (settingsInitialOpen) {
      setShowSettings(true);
      api.updateParameters({ settingsInitialOpen: false });
    }
  }, [settingsInitialOpen, api]);

  const selectedMarkets = markets ?? EMPTY_ARRAY;
  const selectedMarketAccounts = marketAccounts ?? EMPTY_ARRAY;
  const combinedMarketsAndAccounts = useCombinedMarketsAndAccounts(selectedMarketAccounts, selectedMarkets);

  const requestedMarkets = combinedMarketsAndAccounts.length > 0 ? selectedMarkets : security?.Markets;

  const { marketDataSnapshots } = useMarketDataSnapshot({
    currency: security?.BaseCurrency,
    markets: requestedMarkets,
    marketAccounts: selectedMarketAccounts,
    priceIncrement: priceIncrement ?? security?.DefaultPriceIncrement,
    symbol: security?.Symbol,
    depth,
    tag: `PRICE_LADDER_${api.id}`,
    includeTradeDirection: false,
    unifiedLiquidity: enableUnifiedLiquidity ? UnifiedLiquidityEnum.Enabled : UnifiedLiquidityEnum.Disabled,
  });

  useEffect(() => {
    if (height && height > 0) {
      setDepth(Math.floor((height - HEADER_HEIGHT * 2 - ROW_MIN_HEIGHT * 2) / ROW_MIN_HEIGHT / 2));
    }
  }, [height]);

  const primeFromOrderBook = usePrimeFromOrderBook({
    security,
    marketAccounts: marketAccounts,
    subAccount: subAccount,
    orderPresetID,
    aggregation,
  });
  const handleClickRow = useCallback(
    (row: { side: SideEnum; price: string; size?: string; intent?: OrderIntent }) => {
      primeFromOrderBook({
        side: row.side,
        price: row.price,
        intent: row.intent,
        quantity: row.size,
      });
    },
    [primeFromOrderBook]
  );

  const handleChangeIncrement = useCallback(
    (priceIncrement: string) => {
      api.updateParameters({ priceIncrement });
      mixpanel.track(MixpanelEvent.ChangeLadderIncrement);
    },
    [mixpanel, api]
  );

  const handleSymbolChange = useCallback(
    (symbol: string | undefined) => {
      if (!symbol) {
        return;
      }
      const security = securitiesBySymbol.get(symbol);
      if (!customTitle) {
        api.setTitle(symbol ? `${security?.DisplaySymbol} prices` : 'Price Ladder');
      }
      api.updateParameters({
        symbol,
        marketAccounts: isMultileg(security) ? [MULTILEG_MARKET_ACCOUNT] : [],
        markets: [],
        aggregation: undefined,
        priceIncrement: security?.DefaultPriceIncrement,
        enableUnifiedLiquidity: false,
      });
    },
    [api, securitiesBySymbol, customTitle]
  );

  const handleUpdateParameters = useCallback(
    (params: Partial<SingleSymbolPanelConfig>) => {
      api.updateParameters(params);
    },
    [api]
  );

  return (
    <>
      <Panel
        panelApi={api}
        display="flex"
        flexDirection="column"
        background="backgroundContent"
        h="100%"
        data-testid="price-ladder-panel"
      >
        <SingleSymbolPanelHeader
          security={security}
          aggregation={aggregation}
          markets={markets}
          marketAccounts={marketAccounts}
          showSettings={showSettings}
          setShowSettings={setShowSettings}
          enableUnifiedLiquidity={enableUnifiedLiquidity}
          orderPresetID={orderPresetID}
          subAccount={subAccount}
          panelTypeLabel="Price Ladder"
        />
        {showSettings ? (
          <SingleSymbolPanelSettings
            onClose={() => setShowSettings(false)}
            onSymbolChange={handleSymbolChange}
            onUpdateParameters={handleUpdateParameters}
            enableUnifiedLiquidity={enableUnifiedLiquidity}
            marketAccounts={marketAccounts}
            markets={markets}
            aggregation={aggregation}
            symbol={symbol}
            availableSecurities={searchableSecurities}
            allowMultileg={true}
            allowUnifiedLiquidity={true}
            orderPresetID={orderPresetID}
            subAccount={subAccount}
          />
        ) : security ? (
          <PriceLadder
            ref={ref}
            width={width}
            increment={priceIncrement ?? security?.DefaultPriceIncrement}
            onIncrementChange={handleChangeIncrement}
            onClickRow={handleClickRow}
            security={security}
            steps={depth}
            marketDataObservable={marketDataSnapshots}
          />
        ) : (
          <HStack flex="1">No symbol selected</HStack>
        )}
      </Panel>
    </>
  );
}
